import { useEffect, useMemo, useState } from "react";
import api, { formatMYR } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Ticket, Copy, Percent, DollarSign, Truck, Search } from "lucide-react";

const TYPES = [
    { value: "fixed", label: "直接扣指定金额 (RM)", icon: DollarSign },
    { value: "percent", label: "百分比折扣 (%)", icon: Percent },
    { value: "free_shipping", label: "免邮费", icon: Truck },
];

const emptyForm = {
    code: "",
    type: "fixed",
    value: 0,
    min_order: 0,
    max_uses: 0,
    valid_from: "",
    valid_until: "",
    active: true,
    note: "",
};

const randomCode = (len = 6) => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let s = "";
    for (let i = 0; i < len; i++) s += chars[Math.floor(Math.random() * chars.length)];
    return s;
};

export default function CouponsPage() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/coupons");
            setRows(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "加载失败");
        }
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const openCreate = () => {
        setEditing(null);
        setForm({ ...emptyForm, code: randomCode() });
        setDialogOpen(true);
    };
    const openEdit = (r) => {
        setEditing(r);
        setForm({
            code: r.code,
            type: r.type,
            value: r.value,
            min_order: r.min_order,
            max_uses: r.max_uses,
            valid_from: r.valid_from || "",
            valid_until: r.valid_until || "",
            active: r.active !== false,
            note: r.note || "",
        });
        setDialogOpen(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.code.trim()) return toast.error("请输入优惠码");
        setSaving(true);
        try {
            const payload = {
                ...form,
                code: form.code.trim().toUpperCase(),
                value: Number(form.value || 0),
                min_order: Number(form.min_order || 0),
                max_uses: Number(form.max_uses || 0),
            };
            if (editing) {
                await api.put(`/coupons/${editing.id}`, payload);
                toast.success("优惠码已更新");
            } else {
                await api.post("/coupons", payload);
                toast.success("优惠码已创建");
            }
            setDialogOpen(false);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "保存失败");
        } finally {
            setSaving(false);
        }
    };

    const del = async (r) => {
        if (!window.confirm(`确定删除优惠码 ${r.code}？`)) return;
        try {
            await api.delete(`/coupons/${r.id}`);
            toast.success("已删除");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "删除失败");
        }
    };

    const toggleActive = async (r) => {
        try {
            await api.put(`/coupons/${r.id}`, { ...r, active: !r.active });
            load();
        } catch (err) {
            toast.error("切换失败");
        }
    };

    const copyCode = (code) => {
        navigator.clipboard.writeText(code);
        toast.success(`已复制：${code}`);
    };

    const filtered = useMemo(() => {
        if (!q) return rows;
        const t = q.toLowerCase();
        return rows.filter((r) => r.code.toLowerCase().includes(t) || (r.note || "").toLowerCase().includes(t));
    }, [rows, q]);

    const totalActive = rows.filter((r) => r.active).length;
    const totalUses = rows.reduce((n, r) => n + (r.uses_count || 0), 0);

    const describe = (r) => {
        if (r.type === "fixed") return `减 RM ${Number(r.value).toFixed(2)}`;
        if (r.type === "percent") return `${r.value}% off`;
        if (r.type === "free_shipping") return "免邮费";
        return "-";
    };

    return (
        <div className="space-y-6" data-testid="coupons-page">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="text-xs font-semibold uppercase tracking-widest text-gold-500">营销</div>
                    <h1 className="text-3xl font-bold tracking-tight text-slate-900 mt-1">优惠码</h1>
                    <p className="text-sm text-gold-500 mt-1">客户在下单页面输入代码即可享受折扣</p>
                </div>
                <Button data-testid="add-coupon-btn" onClick={openCreate} className="bg-gold-600 hover:bg-gold-700 text-white gap-2">
                    <Plus className="h-4 w-4" /> 新增优惠码
                </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="p-5 border-slate-200 shadow-sm">
                    <div className="text-xs font-semibold uppercase tracking-wider text-gold-500">总优惠码</div>
                    <div className="text-2xl font-bold font-mono text-slate-900 mt-2">{rows.length}</div>
                </Card>
                <Card className="p-5 border-slate-200 shadow-sm">
                    <div className="text-xs font-semibold uppercase tracking-wider text-gold-500">当前生效</div>
                    <div className="text-2xl font-bold font-mono text-gold-700 mt-2">{totalActive}</div>
                </Card>
                <Card className="p-5 border-slate-200 shadow-sm">
                    <div className="text-xs font-semibold uppercase tracking-wider text-gold-500">累计使用</div>
                    <div className="text-2xl font-bold font-mono text-slate-900 mt-2">{totalUses}</div>
                </Card>
            </div>

            <Card className="border-slate-200 shadow-sm">
                <div className="p-4 border-b border-slate-200 flex flex-wrap items-center gap-3">
                    <div className="relative flex-1 min-w-[220px] max-w-sm">
                        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gold-400" />
                        <Input placeholder="搜索优惠码或备注..." className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} />
                    </div>
                </div>
                {loading ? (
                    <div className="p-12 text-center text-gold-500">加载中...</div>
                ) : filtered.length === 0 ? (
                    <div className="p-12 text-center">
                        <Ticket className="h-10 w-10 text-gold-200 mx-auto mb-3" />
                        <div className="text-sm text-gold-500">{rows.length === 0 ? "还没有优惠码，点击右上角新增" : "没有匹配的记录"}</div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-slate-200 bg-gold-50/60">
                                    <th className="text-left py-3 px-6 text-xs font-semibold uppercase tracking-wider text-gold-500">代码</th>
                                    <th className="text-left py-3 px-6 text-xs font-semibold uppercase tracking-wider text-gold-500">类型</th>
                                    <th className="text-right py-3 px-6 text-xs font-semibold uppercase tracking-wider text-gold-500">最低订单</th>
                                    <th className="text-center py-3 px-6 text-xs font-semibold uppercase tracking-wider text-gold-500">使用次数</th>
                                    <th className="text-left py-3 px-6 text-xs font-semibold uppercase tracking-wider text-gold-500">有效期</th>
                                    <th className="text-center py-3 px-6 text-xs font-semibold uppercase tracking-wider text-gold-500">状态</th>
                                    <th className="text-right py-3 px-6 text-xs font-semibold uppercase tracking-wider text-gold-500">操作</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((r) => {
                                    const Icon = (TYPES.find((t) => t.value === r.type) || TYPES[0]).icon;
                                    return (
                                        <tr key={r.id} data-testid={`coupon-row-${r.id}`} className="border-b border-slate-100 hover:bg-gold-50/40 transition-colors">
                                            <td className="py-3 px-6">
                                                <div className="flex items-center gap-2">
                                                    <code className="text-sm font-mono font-bold text-gold-800 bg-gold-100/60 px-2 py-1 rounded">{r.code}</code>
                                                    <button onClick={() => copyCode(r.code)} className="text-gold-400 hover:text-gold-700" title="复制">
                                                        <Copy className="h-3.5 w-3.5" />
                                                    </button>
                                                </div>
                                                {r.note && <div className="text-xs text-slate-500 mt-1">{r.note}</div>}
                                            </td>
                                            <td className="py-3 px-6 text-sm text-slate-700">
                                                <span className="inline-flex items-center gap-1.5">
                                                    <Icon className="h-3.5 w-3.5 text-gold-500" />
                                                    {describe(r)}
                                                </span>
                                            </td>
                                            <td className="py-3 px-6 text-sm text-right font-mono text-slate-600">{r.min_order > 0 ? formatMYR(r.min_order) : "-"}</td>
                                            <td className="py-3 px-6 text-sm text-center font-mono text-slate-700">
                                                {r.uses_count || 0}{r.max_uses > 0 ? ` / ${r.max_uses}` : ""}
                                            </td>
                                            <td className="py-3 px-6 text-xs text-slate-500 font-mono">
                                                {r.valid_from || r.valid_until ? `${r.valid_from || "..."} ~ ${r.valid_until || "..."}` : "无限期"}
                                            </td>
                                            <td className="py-3 px-6 text-center">
                                                <Switch checked={r.active !== false} onCheckedChange={() => toggleActive(r)} data-testid={`coupon-toggle-${r.id}`} />
                                            </td>
                                            <td className="py-3 px-6 text-right">
                                                <div className="flex items-center justify-end gap-1">
                                                    <Button variant="ghost" size="sm" onClick={() => openEdit(r)}><Pencil className="h-3.5 w-3.5" /></Button>
                                                    <Button variant="ghost" size="sm" onClick={() => del(r)} className="text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></Button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-md" data-testid="coupon-dialog">
                    <DialogHeader>
                        <DialogTitle>{editing ? "编辑优惠码" : "新增优惠码"}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={submit} className="space-y-4 pt-2">
                        <div className="space-y-1.5">
                            <Label>代码 *</Label>
                            <div className="flex gap-2">
                                <Input
                                    data-testid="coupon-code-input"
                                    value={form.code}
                                    onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                                    placeholder="例如：NEWYEAR20"
                                    className="font-mono uppercase"
                                    required
                                />
                                <Button type="button" variant="outline" onClick={() => setForm({ ...form, code: randomCode() })}>随机</Button>
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>类型 *</Label>
                            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                                <SelectTrigger data-testid="coupon-type-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {TYPES.map((t) => (
                                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {form.type !== "free_shipping" && (
                            <div className="space-y-1.5">
                                <Label>{form.type === "fixed" ? "扣多少 (RM)" : "折扣百分比 (%)"}</Label>
                                <Input
                                    data-testid="coupon-value-input"
                                    type="number"
                                    step={form.type === "fixed" ? "0.01" : "0.1"}
                                    min="0"
                                    max={form.type === "percent" ? "100" : undefined}
                                    value={form.value}
                                    onChange={(e) => setForm({ ...form, value: e.target.value })}
                                    className="font-mono"
                                />
                            </div>
                        )}
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>最低订单 (RM)</Label>
                                <Input type="number" step="0.01" min="0" value={form.min_order} onChange={(e) => setForm({ ...form, min_order: e.target.value })} className="font-mono" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>使用次数上限</Label>
                                <Input type="number" min="0" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} className="font-mono" placeholder="0 = 无限制" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>生效日</Label>
                                <Input type="date" value={form.valid_from} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} className="font-mono" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>失效日</Label>
                                <Input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} className="font-mono" />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>备注</Label>
                            <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="例如：新年促销" />
                        </div>
                        <div className="flex items-center justify-between p-3 rounded-md border border-slate-200 bg-gold-50/40">
                            <span className="text-sm text-slate-700">当前状态：<Badge className={form.active ? "bg-gold-100 text-gold-800" : "bg-slate-100 text-slate-500"}>{form.active ? "生效" : "停用"}</Badge></span>
                            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
                        </div>
                        <DialogFooter className="pt-2">
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>取消</Button>
                            <Button type="submit" data-testid="save-coupon-btn" disabled={saving} className="bg-gold-600 hover:bg-gold-700 text-white">
                                {saving ? "保存中..." : editing ? "保存修改" : "创建"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
